import React from 'react';

import { LucideIcon } from 'lucide-react';

interface ButtonProps {
  onClick?: () => void;
  type?: 'button' | 'submit';
  children: React.ReactNode;
  className?: string; // Additional className
  variant?: 'primary' | 'secondary' | 'success' | 'danger' | 'disabled'; // Button variants
  disabled?: boolean;
  isLoading?: boolean;
  icon?: LucideIcon;
  iconPosition?: 'left' | 'right';
}

const Button: React.FC<ButtonProps> = ({
  onClick,
  type = 'button',
  children,
  className,
  variant = 'primary',
  disabled = false,
  isLoading = false,
  icon: Icon,
  iconPosition = 'left',
}) => {
  const baseClasses =
    'inline-flex items-center justify-center px-6 py-2.5 transition-colors text-sm font-semibold rounded-md text-nowrap focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 dark:focus-visible:ring-offset-secondary';

  // Define classes for each button variant with dark mode styles.
  //
  // Text on a gold fill is BLACK, never white. White on gold is 2.32:1 and unreadable; black on
  // gold is 9.05:1. This is rule one in GolfPlayed.Web/brand/BRAND.md and it is not negotiable.
  //
  // Gold survives dark mode unchanged, so primary keeps its brand colours in both themes rather
  // than falling back to grey as it did before, which threw the brand away exactly where gold
  // reads best.
  const variantClasses = {
    primary: 'bg-primary text-secondary hover:bg-opacity-90 cursor-pointer',
    secondary:
      'border border-secondary text-ink bg-white hover:bg-secondary hover:text-white dark:border-white/30 dark:bg-transparent dark:text-white dark:hover:bg-white dark:hover:text-secondary cursor-pointer',
    // success/danger are state colours, not brand colours. The brand pack has no semantic state
    // palette (BRAND.md section 4), so these stay on Tailwind's own until it does.
    success:
      'bg-green-600 text-white hover:bg-green-700 dark:bg-green-600 dark:hover:bg-green-500 cursor-pointer',
    danger:
      'bg-red-600 text-white hover:bg-red-700 dark:bg-red-600 dark:hover:bg-red-500 cursor-pointer',
    disabled:
      'bg-gray-200 text-ink-muted cursor-not-allowed hover:bg-gray-200 dark:bg-white/10 dark:text-white/40',
  };

  const renderContent = () => {
    if (isLoading) {
      return (
        <div className="flex items-center justify-center">
          {children}
          {/* The gap is transparent, not white. The ring is currentColor, so on a gold button
              it is black; a white segment there would be the forbidden white-on-gold. */}
          <span className="ml-2 spinner-border animate-spin inline-block w-4 h-4 border-2 rounded-full border-t-transparent" />
        </div>
      );
    }

    return (
      <>
        {Icon && iconPosition === 'left' && <Icon className="w-4 h-4 mr-2" />}
        {children}
        {Icon && iconPosition === 'right' && <Icon className="w-4 h-4 ml-2" />}
      </>
    );
  };

  // Apply base classes, variant classes, and additional className
  return (
    <button
      type={type}
      onClick={onClick}
      className={`${baseClasses} ${variantClasses[variant]} ${className || ''}`}
      disabled={disabled}
    >
      {renderContent()}
    </button>
  );
};

export default Button;
